import {
  languageSchema,
  Member,
  memberSchema,
  memberTypeSchema,
  User,
  objectIdSchema,
  mainOrgTypesSchema,
  emailSchema,
  optionalStringSchema,
  paginationWithSearchSchema,
} from '@hero/schema';
import { MembersAllData, API, MembersCountAPIData } from '..';
import { z } from 'zod';

//* create api
export const memberCreateAPISchema = memberSchema.omit({ _id: true, userId: true }).extend({
  /** the preferred language for the user to be added. */
  preferredLanguage: languageSchema.optional(),
  email: optionalStringSchema(emailSchema),
});
export type MemberCreateAPI = API<z.infer<typeof memberCreateAPISchema>, Member>;

//* update api
export const memberUpdateAPISchema = memberSchema
  .omit({ userId: true })
  .partial()
  .required({ _id: true, type: true })
  .extend({
    /**
     * A driver's number changed: whether the old number stays on the
     * company. Default — kept only when the old number is the person
     * making the change.
     */
    keepOldUserOnOrg: z.boolean().optional(),
    /**
     * A driver's account another org also uses keeps its own name; true
     * renames it too. An account only this org uses follows the driver's
     * name without it. See `Member.sharedAccount`.
     */
    renameAccount: z.boolean().optional(),
  });

export type MemberUpdateAPI = API<z.infer<typeof memberUpdateAPISchema>, Member>;

//* update self
export const memberUpdateCurrentAPISchema = memberUpdateAPISchema.omit({ _id: true, type: true });

/** updates the member */
export type MemberCurrentUpdateAPI = API<
  z.infer<typeof memberUpdateCurrentAPISchema>,
  { member: Member; user: User }
>;

//* get all api
export const memberGetAllAPISchema = paginationWithSearchSchema;
export type MemberGetAllAPI = API<z.infer<typeof memberGetAllAPISchema>, MembersAllData>;

/** Get current member */
export type MemberGetCurrentAPI = API<undefined, Member>;

//* driver members (carrier fleet list)
export const memberDriverListStatusFilterSchema = z
  .enum(['available', 'busy', 'no_vehicle'])
  .describe(
    "Driver-member list filter. 'available' ↔ Driver.status='active'. 'busy' ↔ Driver.status='busy'. 'no_vehicle' = no assigned vehicle (regardless of status). Same pattern as `driverCarrierListStatusFilterSchema`.",
  );
export const memberDriversAPISchema = paginationWithSearchSchema.extend({
  status: memberDriverListStatusFilterSchema.optional(),
});

/** get all driver member in a carrier company */
export type MemberDriversAPI = API<z.infer<typeof memberDriversAPISchema>, Member[]>;

//* user members
export const memberUsersAPISchema = paginationWithSearchSchema.extend({
  /** filter by member type (admin / dispatcher / employee) */
  type: memberTypeSchema.exclude(['driver']).optional(),
});
/** get all user members in a carrier company
 *
 * users are all `NON driver` members such as admins / dispatchers / etc
 */
export type MemberUsersAPI = API<z.infer<typeof memberUsersAPISchema>, Member[]>;

/** get all user members in a carrier company
 *
 * users are all `NON driver` members such as admins / dispatchers / etc
 */
export type MemberCountAPI = API<undefined, MembersCountAPIData>;

//* get one member by id
export const memberGetOneAPISchema = z.object({
  _id: objectIdSchema,
});
export type MemberGetOneAPI = API<z.infer<typeof memberGetOneAPISchema>, Member>;

//* delete member
export const memberDeleteAPISchema = memberUpdateAPISchema.pick({ _id: true, type: true });
export type MemberDeleteAPI = API<z.infer<typeof memberDeleteAPISchema>, undefined>;

// ====== ADMIN APIs ======
export const memberCreateAdminAPISchema = memberCreateAPISchema.extend({
  orgId: objectIdSchema,
  orgType: mainOrgTypesSchema,
});
export type MemberCreateAdminAPI = API<z.infer<typeof memberCreateAdminAPISchema>, Member>;

// Admin get all members for any organization
export const memberGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  orgId: objectIdSchema,
  memberType: z.enum(['all', 'drivers', 'users']).optional().default('users'),
});
export type MemberGetAllAdminAPI = API<z.infer<typeof memberGetAllAdminAPISchema>, Member[]>;

export const memberGetOneAdminAPISchema = z.object({
  /** the member user id or driver id */
  _id: objectIdSchema,
  /** optionally supply the org id that this member is related to ...
   * because sometimes a member is related to multiple orgs. So you might to see the result for a specific org.
   * It's ok if you don't provide it. We will try to guess it. */
  orgId: objectIdSchema.optional(),
});
export type MemberGetOneAdminAPI = API<z.infer<typeof memberGetOneAdminAPISchema>, Member>;
